import { useEffect, useState } from 'react';
import { Loader, Group, Table } from '@mantine/core';
import Header from '../../../components/util/header';
import { useSelector } from 'react-redux';
import { fetchAPMPeriodsList, IAPMPeriodsResponse } from '../../../../libraries/apm';
import { getLocationName } from '../../../utils/script';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import PaginationComponent from '../../../components/pagination';
import { LoginState } from '../../../client-redux/login/reducer';
import APMReceipt from '../../../components/apm-receipt';
import FilterBar, { FilterBarFieldsData } from '../../../components/filterbar/index';
import ShowComissionsDetails from '../../../components/show-comissions-details';
import ShowTransactionsDetails from '../../../components/show-transactions-details';
import { fetchAPMPeriodsPDF, fetchAPMPeriodsXLS } from '../../../../libraries/transactions';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { formatDateTimeLabel, formatDateToISO } from '../../../utils/date';
import './styles.scss';

const APMPeriods = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const user = useSelector<AppState, LoginState>(state => state.login);
  const controller = new AbortController();

  const [loading, setLoading] = useState(false);
  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [list, setList] = useState<IAPMPeriodsResponse>();
  const [noResults, setNoResults] = useState<boolean>(true);
  const [statusOperCode, setStatusOperCode] = useState<number | undefined>();

  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  useEffect(() => {
    if (filterBarData && limit) {
      loadAPMPeriodsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadAPMPeriodsList = () => {
    if (loading) {
      controller.abort();
    }
    if (!filterBarData || !limit) {
      return;
    }
    setLoading(true);
    setList(undefined);
    const shouldFetchTotalRows = offset === 0;

    fetchAPMPeriodsList(
      controller,
      filterBarData.entity || user.user?.entid || 0,
      filterBarData.lid || 0,
      filterBarData.posid || 0,
      offset,
      limit,
      shouldFetchTotalRows,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime)
    )
      .then(response => {
        if (response.statusOper.code === 0) {
          setList(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getXLSReport = () => {
    if (!filterBarData) return;
    return fetchAPMPeriodsXLS(
      controller,
      filterBarData.entity || 0,
      filterBarData.lid || 0,
      offset,
      limit,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime)
    );
  };

  const getPDFReport = () => {
    if (!filterBarData) return;
    return fetchAPMPeriodsPDF(
      controller,
      filterBarData.entity || 0,
      filterBarData.lid || 0,
      offset,
      limit,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime)
    );
  };

  return (
    <div className="APMPeriods">
      <Header pageTitle={lang.APM_PERIODS} />

      <FilterBar
        onSearch={loadAPMPeriodsList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          lid: true,
          posid: true,
          beginDateTime: true,
          endDateTime: true
        }}
        xlsReport={!noResults ? getXLSReport : undefined}
        pdfReport={!noResults ? getPDFReport : undefined}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>POS</Table.Th>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>{lang.GLOBAL_PERIOD}</Table.Th>
              <Table.Th>{lang.GLOBAL_DATE_TIME_CLOSE}</Table.Th>
              <Table.Th>{lang.TRANSACTION_NUMBERS_FULL}</Table.Th>
              <Table.Th>{lang.TRANSACTION_AMOUNT_CHARGED}</Table.Th>
              <Table.Th>{lang.TRANSACTION_COMISSION_AMOUNT}</Table.Th>
              <Table.Th>{lang.TRANSACTION_AMOUNT_CREDITED}</Table.Th>
              <Table.Td></Table.Td>
              <Table.Td></Table.Td>
              <Table.Td></Table.Td>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={12}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {noResults ? (
                  <Table.Tr>
                    <Table.Td colSpan={12} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.periods?.map((period, index) => {
                    return (
                      <Table.Tr key={index}>
                        <Table.Td>{period.key.a001}</Table.Td>
                        <Table.Td>{getLocationName(period.sid)}</Table.Td>
                        <Table.Td>{period.key.a031}</Table.Td>
                        <Table.Td>{formatDateTimeLabel(period.dateTime)}</Table.Td>
                        <Table.Td>{period.totalTransactions}</Table.Td>
                        <Table.Td>{(period.amount / 100).toFixed(2) + ' €'}</Table.Td>
                        <Table.Td>{(period.amountCommitions / 100).toFixed(2) + ' €'}</Table.Td>
                        <Table.Td>{(period.amountWithoutCommitions / 100).toFixed(2) + ' €'}</Table.Td>
                        <Table.Td>
                          <ShowComissionsDetails
                            a001={period.key.a001}
                            movType={period.key.movType}
                            a031={period.key.a031}
                            a032={period.key.a032}
                            a029={period.key.a029}
                            dateTime={period.key.dateTime}
                          />
                        </Table.Td>
                        <Table.Td>
                          <ShowTransactionsDetails
                            a001={period.key.a001}
                            movType={period.key.movType}
                            a031={period.key.a031}
                            a032={period.key.a032}
                            a029={period.key.a029}
                            dateTime={period.key.dateTime}
                          />
                        </Table.Td>
                        <Table.Td>
                          <APMReceipt
                            a001={period.key.a001}
                            movType={period.key.movType}
                            a031={period.key.a031}
                            a032={period.key.a032}
                            a029={period.key.a029}
                            dateTime={period.key.dateTime}
                          />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default APMPeriods;
